import apiConfig from '../apiConfig';
import { syncUnreadAppBadge } from './appBadge';
import { getCustomerAuthHeaders } from './auth/customerAuthToken';

type PushConfigResponse = {
  enabled?: boolean;
  public_key?: string | null;
};

const SERVICE_WORKER_TIMEOUT_MS = 10000;
const REQUEST_TIMEOUT_MS = 10000;

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> => {
  let timer = 0;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = window.setTimeout(() => {
          reject(new Error(`${label} timed out`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
};

const fetchWithTimeout = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    return await fetch(input, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timer);
  }
};

export const detectPushSupport = (): boolean => {
  try {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  } catch {
    return false;
  }
};

export const getNotificationPermissionSafely = (): NotificationPermission => {
  try {
    if ('Notification' in window && typeof Notification.permission === 'string') {
      return Notification.permission;
    }
  } catch {
    return 'default';
  }

  return 'default';
};

export const fetchPushConfig = async (): Promise<PushConfigResponse | null> => {
  const res = await fetchWithTimeout(`${apiConfig.baseURL}/api/customer-app/push-subscriptions/config`);
  if (!res.ok) {
    throw new Error('Failed to load push config');
  }

  return (await res.json()) as PushConfigResponse;
};

export const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration> => {
  const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
  let registration = await navigator.serviceWorker.getRegistration();

  if (!registration) {
    registration = await navigator.serviceWorker.register(swUrl);
  }

  return withTimeout(
    navigator.serviceWorker.ready.then((readyRegistration) => readyRegistration || (registration as ServiceWorkerRegistration)),
    SERVICE_WORKER_TIMEOUT_MS,
    'Service worker setup',
  );
};

export const savePushSubscriptionForCustomer = async (
  _customerId: number,
  subscription: PushSubscription,
): Promise<void> => {
  const json = subscription.toJSON();
  const res = await fetchWithTimeout(`${apiConfig.baseURL}/api/customer-app/push-subscriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCustomerAuthHeaders(),
    },
    body: JSON.stringify({
      subscription: {
        endpoint: json.endpoint ?? subscription.endpoint,
        keys: {
          p256dh: json.keys?.p256dh ?? '',
          auth: json.keys?.auth ?? '',
        },
      },
      user_agent: navigator.userAgent,
    }),
  });

  if (!res.ok) {
    throw new Error('Failed to save push subscription');
  }
};

export const ensurePushReadyForDevice = async (customerId?: number | null): Promise<boolean> => {
  if (!detectPushSupport()) return false;

  const config = await fetchPushConfig();
  if (!config?.enabled || !config.public_key) return false;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const registration = await getServiceWorkerRegistration();
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await withTimeout(
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      }),
      SERVICE_WORKER_TIMEOUT_MS,
      'Push subscription',
    );
  }

  if (subscription) {
    await savePushSubscriptionForCustomer(customerId ?? 0, subscription);
    void syncUnreadAppBadge();
  }

  return true;
};

export const syncPushSubscriptionForCustomer = async (customerId: number): Promise<boolean> => {
  if (!detectPushSupport()) return false;
  if (getNotificationPermissionSafely() !== 'granted') return false;

  const config = await fetchPushConfig();
  if (!config?.enabled || !config.public_key) return false;

  const registration = await getServiceWorkerRegistration();
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await withTimeout(
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      }),
      SERVICE_WORKER_TIMEOUT_MS,
      'Push subscription',
    );
  }

  if (!subscription) return false;

  await savePushSubscriptionForCustomer(customerId, subscription);
  void syncUnreadAppBadge();
  return true;
};
